import {
  faArrowsRotate,
  faCheck,
  faChevronDown,
  faChevronUp,
  faClockRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import iconNode from 'src/components/ui/iconNode';
import MetaLine from 'src/components/ui/MetaLine';
import StatusBadge from 'src/components/ui/StatusBadge';
import { REPORT_TYPE_LABELS } from 'src/constants/maintenance';
import {
  getRequirementInstructions,
  instructionHasReport,
  summarizeFrequency,
} from 'src/utils/maintenance';

const RequirementCard = ({
  requirement,
  anchor,
  isInstance,
  lastPerformed,
  defaultOpen,
  reports,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const instructions = getRequirementInstructions(requirement);
  const hasInstructions = instructions.length > 0;
  const scopeIcon = isInstance ? anchor.instanceIcon : anchor.typeIcon;
  const isInstructionAnswered = instruction => instructionHasReport(reports, instruction);

  return (
    <div className='mnt-reqcard'>
      <div
        className='mnt-reqcard__head'
        role='button'
        tabIndex={0}
        onClick={() => setOpen(prev => !prev)}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') setOpen(prev => !prev);
        }}
      >
        <span
          className={classNames(
            'mnt-scope-tile',
            isInstance ? 'mnt-scope-tile--instance' : 'mnt-scope-tile--type'
          )}
        >
          {iconNode(scopeIcon)}
        </span>
        <div className='mnt-reqcard__main'>
          <div className='mnt-reqcard__name'>{requirement.name}</div>
          <MetaLine icon={faArrowsRotate}>{summarizeFrequency(requirement)}</MetaLine>
        </div>
        <span className='mnt-reqcard__aside'>
          {!hasInstructions && (
            <StatusBadge size='sm' uppercase={false}>
              No steps
            </StatusBadge>
          )}
          {!open && hasInstructions && (
            <span className='mnt-reqcard__count'>{instructions.length}</span>
          )}
          <FontAwesomeIcon
            icon={open ? faChevronUp : faChevronDown}
            className='mnt-reqcard__chevron'
          />
        </span>
      </div>
      {open && (
        <div className='mnt-reqcard__body'>
          <div className='mnt-reqcard__history'>
            <FontAwesomeIcon icon={faClockRotateLeft} />
            {lastPerformed ? (
              <span>
                Last performed <span className='mnt-reqcard__history-date'>{lastPerformed}</span>
              </span>
            ) : (
              <span className='mnt-reqcard__history-never'>Never performed</span>
            )}
          </div>
          <div className='mnt-reqcard__steps'>
            {hasInstructions ? (
              instructions.map((instruction, index) => {
                const answered = isInstructionAnswered(instruction);
                return (
                  <div key={instruction.uuid || index} className='mnt-step-row'>
                    <span
                      className={classNames('mnt-step-row__num', {
                        'mnt-step-row__num--done': answered,
                      })}
                    >
                      {answered ? <FontAwesomeIcon icon={faCheck} /> : index + 1}
                    </span>
                    <span className='mnt-step-row__q'>{instruction.question}</span>
                    <StatusBadge size='sm' uppercase={false}>
                      {REPORT_TYPE_LABELS[instruction.reportType] || instruction.reportType}
                    </StatusBadge>
                  </div>
                );
              })
            ) : (
              <div className='mnt-reqcard__empty'>
                This requirement has no checklist steps. There is nothing to record; it counts as
                done once you complete the event.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

RequirementCard.propTypes = {
  requirement: PropTypes.object.isRequired,
  anchor: PropTypes.object.isRequired,
  isInstance: PropTypes.bool.isRequired,
  lastPerformed: PropTypes.string,
  defaultOpen: PropTypes.bool,
  reports: PropTypes.array,
};

RequirementCard.defaultProps = {
  lastPerformed: null,
  defaultOpen: false,
  reports: [],
};

export default RequirementCard;
